import { Helmet } from "react-helmet-async";

// Organization Schema for the homepage
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Gogenia.ai",
    "alternateName": "Gogenia",
    "url": "https://gogenia.ai",
    "logo": "https://gogenia.ai/logo.png",
    "description": "Agence spécialisée en Generative Engine Optimization (GEO) pour optimiser votre visibilité dans ChatGPT, Gemini et Perplexity.",
    "foundingDate": "2024",
    "areaServed": {
      "@type": "Country",
      "name": "France"
    },
    "serviceType": ["GEO Optimization", "AI Visibility", "Generative Engine Optimization"],
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "contact@gogenia.ai",
      "contactType": "customer service",
      "availableLanguage": ["French", "English"]
    },
    "sameAs": [
      "https://www.linkedin.com/company/gogenia/",
      "https://twitter.com/gogenia_ai"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// WebSite Schema with SearchAction
export const WebSiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Gogenia.ai",
    "url": "https://gogenia.ai",
    "description": "Optimisez votre présence dans les IA génératives avec Gogenia.ai",
    "inLanguage": ["fr-FR", "en-US"],
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://gogenia.ai/blog?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Service Schema for services offered
export const ServicesSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Generative Engine Optimization",
    "provider": {
      "@type": "Organization",
      "name": "Gogenia.ai"
    },
    "areaServed": {
      "@type": "Country",
      "name": "France"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services GEO",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Audit GEO",
            "description": "Analyse complète de votre visibilité dans les moteurs IA génératifs"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Optimisation GEO",
            "description": "Optimisation de votre contenu pour ChatGPT, Gemini et Perplexity"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Suivi de Performance",
            "description": "Dashboard de suivi de vos citations et mentions IA"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Formation GEO",
            "description": "Formation de vos équipes aux meilleures pratiques GEO"
          }
        }
      ]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQ Schema
interface FAQItem {
  question: string;
  answer: string;
}

export const FAQSchema = ({ faqs }: { faqs: FAQItem[] }) => {
  if (!faqs || faqs.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Article Schema for blog posts
interface ArticleSchemaProps {
  title: string;
  description: string;
  imageUrl?: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  url: string;
  readTime?: string;
  wordCount?: number;
}

export const ArticleSchema = ({ 
  title, 
  description, 
  imageUrl, 
  datePublished, 
  dateModified,
  authorName = "Équipe Gogenia.ai",
  url,
  readTime,
  wordCount
}: ArticleSchemaProps) => {
  // Parse read time to ISO 8601 duration format (e.g., "5 min" -> "PT5M")
  const parseReadTime = (time?: string): string | undefined => {
    if (!time) return undefined;
    const match = time.match(/(\d+)/);
    if (match) {
      return `PT${match[1]}M`;
    }
    return undefined;
  };

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": imageUrl || "https://gogenia.ai/og-image.png",
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": "Organization",
      "name": authorName,
      "url": "https://gogenia.ai"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Gogenia.ai",
      "logo": {
        "@type": "ImageObject",
        "url": "https://gogenia.ai/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    }
  };

  // Add optional time-related properties
  const timeRequired = parseReadTime(readTime);
  if (timeRequired) {
    schema.timeRequired = timeRequired;
  }
  if (wordCount) {
    schema.wordCount = wordCount;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// BreadcrumbList Schema
interface BreadcrumbItem {
  name: string;
  url: string;
}

export const BreadcrumbSchema = ({ items }: { items: BreadcrumbItem[] }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// LocalBusiness Schema (optional, for local SEO)
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Gogenia.ai",
    "description": "Agence de référencement IA spécialisée en Generative Engine Optimization",
    "url": "https://gogenia.ai",
    "email": "contact@gogenia.ai",
    "priceRange": "€€€",
    "areaServed": "France",
    "serviceArea": {
      "@type": "Country",
      "name": "France"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};